import React, { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import classNames from 'classnames';
import Typography from '@lib/components/Typography/Typography';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import {
  AppendValue,
  FieldArrayItemRenderAddMore,
  FieldArrayItemType,
} from '@lib/components/ReactHookForm/types';
import Trans from '@lib/components/Trans/Trans';
import Search from '@lib/components/Search/Search';
import getLastPosition from '@lib/utils/getLastPosition';
import getAppendValue from 'components/FormTemplateBuilder/QuestionCheckboxButtonGridField/utils/getAppendValue';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import LinkComponent from '@lib/components/Link/Link';
import { getNewOptionValue } from '@lib/utils/formOptionsHelpers';
import styles from './QuestionChecklistField.module.scss';
import getVisibleOptionsIds from './utils/getVisibleOptionsIds';

interface ChildrenProps {
  fieldItem: FieldArrayItemType;
  index: number;
}

interface Props {
  // appendValue?: AppendValue;
  children: (props: ChildrenProps) => React.ReactNode;
  fields: FieldArrayItemType[];
  renderAddMore: FieldArrayItemRenderAddMore;
  renderSettings?: React.ReactNode;
  parentName: string;
}

function QuestionChecklistField(props: Props) {
  const { children, fields, renderAddMore, renderSettings, parentName } = props;
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const { getFieldState, setValue } = useFormContext();
  const { error } = getFieldState(parentName);
  const listRef = useRef<HTMLDivElement>(null);
  const [showGradient, setShowGradient] = useState(false);

  const hasError = !!error;
  const errorsIndexes: number[] = [];
  if (Array.isArray(error)) {
    error.forEach((err, index) => {
      if (err) {
        errorsIndexes.push(index);
      }
    });
  }

  // if (hasError && fields.length > 0) {
  //   const valueMap = new Map<string, number[]>();
  //
  //   fields.forEach((opt, index) => {
  //     const trimmed = opt.values?.[0]?.trim();
  //
  //     if (!trimmed) {
  //       errorsIndexes.push(index);
  //     }
  //
  //     if (!valueMap.has(trimmed)) {
  //       valueMap.set(trimmed, []);
  //     }
  //     valueMap.get(trimmed)?.push(index);
  //   });
  //
  //   const duplicateIndexes = Array.from(valueMap.values())
  //     .filter((indexes) => indexes.length > 1)
  //     .flat();
  //
  //   duplicateIndexes.forEach((index) => {
  //     if (!errorsIndexes.includes(index)) {
  //       errorsIndexes.push(index);
  //     }
  //   });
  // }

  const visibleOptionsIds = getVisibleOptionsIds(fields, searchValue);

  const checkScroll = () => {
    const el = listRef.current;
    if (!el) return;
    setShowGradient(
      el.scrollHeight > el.clientHeight &&
        el.scrollTop + el.clientHeight < el.scrollHeight - 2,
    );
  };

  const handleScroll = () => {
    checkScroll();
  };

  const onSearchHandler = (v: string) => {
    setSearchValue(v);
  };

  const getRenderAddMoreArgs = (): [AppendValue, () => void] => {
    const lastPosition = getLastPosition(fields);
    const group = null;
    return [getAppendValue(group, lastPosition), () => setSearchValue('')];
  };

  const getAddMore = () => {
    const [appendValue, appendCallback] = getRenderAddMoreArgs();
    return renderAddMore(appendValue, appendCallback);
  };

  const onClearList = () => {
    setValue(parentName, [getNewOptionValue(0)], {
      shouldValidate: true,
      shouldDirty: false,
    });
  };

  const onClearSearch = () => setSearchValue('');

  useEffect(() => {
    checkScroll();
  }, [fields, searchValue]);

  useEffect(() => {
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const emptyStateActions = [
    <Button
      key="back"
      buttonText={t('go-back')}
      buttonType={ButtonTypes.primaryFilled}
      leftIcon="arrow_back"
      onClick={onClearSearch}
    />,
  ];

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <Typography variant="body">{t('checklist-options')}</Typography>
          <Tooltip body={t('checklist-field-desc')}>
            <MaterialIcon
              icon="info"
              size="md-16"
              symbolsOutlined
              className={styles.infoBoxIcon}
            />
          </Tooltip>
        </div>
        {fields.length > 1 ? (
          <LinkComponent onClick={onClearList}>{t('clear-list')}</LinkComponent>
        ) : (
          <div />
        )}
        <Typography variant="body" className={styles.headerDesc}>
          <Trans
            i18nKey="options-total-count"
            values={{ total: fields?.length || 0 }}
          />
        </Typography>
      </div>
      {fields?.length > 10 && (
        <Search
          onChange={onSearchHandler}
          placeholder={t('options-search-placeholder')}
          useSearchParam={false}
          value={searchValue}
        />
      )}
      <div className={styles.listWrap}>
        {visibleOptionsIds?.length === 0 ? (
          <ListEmptyState
            title={t('no-results-found')}
            text={t('no-results-found-text')}
            actions={emptyStateActions}
          />
        ) : (
          <div
            className={classNames(styles.list, {
              [styles.listWithError]: hasError,
            })}
          >
            <div
              className={styles.listScroll}
              ref={listRef}
              onScroll={handleScroll}
            >
              {fields.map((fieldItem, index) => (
                <div
                  key={fieldItem.id}
                  className={classNames(styles.listItem, {
                    [styles.listItemWithError]: errorsIndexes.includes(index),
                    [styles.listItemHidden]: !visibleOptionsIds.some(
                      (id) => id === fieldItem.id,
                    ),
                  })}
                >
                  <div className={styles.listRow}>
                    <span className={styles.listItemIndex}>{index + 1}</span>
                    {children({ fieldItem, index })}
                  </div>
                </div>
              ))}
            </div>
            {showGradient && <span className={styles.gradient} />}
          </div>
        )}
        {getAddMore()}
      </div>
      {renderSettings}
    </>
  );
}

export default memo(QuestionChecklistField);
